/**
 * ReportTable — sortable, searchable data table for report results.
 * Supports drill-down navigation, inline line-item sub-rows,
 * and three report formats: header_only, line_only, combo.
 * Fully generic — field names, value computation, and layout all driven by config.
 */
import React, { useState, useMemo } from 'react';
import { Input } from '../../components/ui/input';
import { SortableTableHeader, sortData } from '../../components/SortableTableHeader';
import { useNavigate } from 'react-router-dom';
import { Search, ExternalLink } from 'lucide-react';
import { fmtCurrency, fmtCurrencyCode, fmtPct, fmtDateTz, fmtDateOnlyTz, isDateKey } from './reportUtils';

const SHORT_LABELS = { 'Created By User Id': 'Created By', 'Created By User': 'Created By' };

/** Get a value from a row with fallback field names */
const getVal = (row, key) => {
  if (row[key] !== undefined && row[key] !== null && row[key] !== '') return row[key];
  const fb = {
    delivery_point_code: ['delivery_code'], delivery_code: ['delivery_point_code'],
    document_type_name: ['document_type'], document_type: ['document_type_name'],
    invoice_point_name: ['invoice_code'], invoice_code: ['invoice_point_name'],
    order_date: ['created_at'], issued_date: ['issue_date'], entry_date: ['created_at'],
  };
  for (const alt of (fb[key] || [])) {
    if (row[alt] !== undefined && row[alt] !== null && row[alt] !== '') return row[alt];
  }
  return '';
};

/** Compute document value from valueFields list */
const computeDocValue = (row, valueFields) => {
  if (!valueFields?.length) return 0;
  for (const f of valueFields) {
    const v = row[f];
    if (v !== undefined && v !== null && v !== '' && v !== 0) return parseFloat(v);
  }
  return 0;
};

/** Format a cell value using column config properties */
const fmtCell = (val, col, row, tz) => {
  if (val === null || val === undefined || val === '') return '\u2014';
  if (col.currency || col.key === '_doc_value') return fmtCurrencyCode(val, row?._currency || row?.currency || 'USD');
  if (col.pct || col.key === 'discount_pct') return fmtPct(val);
  if (col.qty) return Number(val).toFixed(2);
  if (isDateKey(col.key) || ['order_date', 'issued_date', 'entry_date', 'invoice_date', 'due_date', 'received_date', 'item_due_date'].includes(col.key))
    return fmtDateOnlyTz(val, tz);
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  return String(val);
};

export default function ReportTable({ rows, columns, tz, drillDown, lineItemColumns, showLineItems, reportFormatConfig, customCols, systemCurrency }) {
  const [sort, setSort] = useState({ field: '', dir: 'asc' });
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  if (!rows?.length) return <div className="text-center py-6 text-muted-foreground text-xs border rounded-xl" data-testid="report-no-data">No data returned</div>;

  // Dispatch to format-specific renderers
  if (reportFormatConfig) {
    const sharedProps = { rows, config: reportFormatConfig, tz, drillDown, search, setSearch, sort, setSort, navigate, customCols };
    if (reportFormatConfig.type === 'combo') return <ComboFormatTable {...sharedProps} />;
    if (reportFormatConfig.value === 'header_only') return <HeaderOnlyTable {...sharedProps} />;
    if (reportFormatConfig.value === 'line_only') return <LineOnlyTable {...sharedProps} />;
  }

  // ── Default table rendering (existing behavior for non-format reports) ──
  const sysCurrency = systemCurrency || 'USD';
  const cols = (columns?.length ? columns : Object.keys(rows[0])
    .filter(k => k !== 'line_items' && k !== 'lines' && k !== '_isParent' && k !== '_isLineItem' && k !== '_parentRef')
    .map(k => ({ key: k, label: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), align: (typeof rows[0][k] === 'number') ? 'right' : 'left' }))
  ).map(c => {
    let label = SHORT_LABELS[c.label] || c.label;
    if (c.tzLabel && tz) {
      try { const abbr = new Date().toLocaleTimeString('en-US', { timeZone: tz, timeZoneName: 'short' }).split(' ').pop(); label = `${label} (${abbr})`; } catch {}
    }
    // Append system currency to monetary column headers
    if (c.currency || /price|cost|value|amount|budget|gross|net_price|net_total|spend|total_value|total_cost|loss_value/i.test(c.key)) {
      const cur = c._labelCurrency || sysCurrency;
      if (!label.includes(`(${cur})`)) label = `${label} (${cur})`;
    }
    return { ...c, label };
  });

  // Build width map from customCols
  const widthMap = {};
  if (customCols) { customCols.forEach(c => { if (c.width) widthMap[c.key] = c.width; }); }

  const handleSort = (field, dir) => setSort({ field, dir });
  let filtered = rows;
  if (search) { const s = search.toLowerCase(); filtered = rows.filter(r => Object.values(r).some(v => String(v || '').toLowerCase().includes(s))); }
  const sorted = sort.field ? sortData(filtered, sort.field, sort.dir) : filtered;

  const displayRows = [];
  const liCols = showLineItems && lineItemColumns ? lineItemColumns : null;
  for (const row of sorted.slice(0, 500)) {
    displayRows.push({ ...row, _rowType: 'parent' });
    if (liCols && row.line_items?.length > 0) {
      for (const li of row.line_items) {
        const liRow = { ...row, _rowType: 'lineItem' };
        delete liRow.line_items;
        liRow.li_item_code = li.item_code || ''; liRow.li_item_description = li.item_description || li.description || '';
        liRow.li_quantity = li.quantity || 0; liRow.li_unit_price = li.unit_price || 0;
        liRow.li_net_price = li.net_price || li.total || 0;
        liRow.li_account_code = li.account_code || ''; liRow.li_category_code = li.category_code || '';
        displayRows.push(liRow);
      }
    }
  }

  const handleRowNav = (row) => {
    if (!drillDown || row._rowType === 'lineItem') return;
    const val = row[drillDown.keyField]; if (!val) return;
    if (drillDown.param) navigate(`${drillDown.route}?${drillDown.param}=${encodeURIComponent(val)}`);
    else navigate(`${drillDown.route}/${encodeURIComponent(val)}`);
  };

  const allCols = liCols ? [...cols, ...liCols] : cols;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-muted-foreground">{filtered.length} row{filtered.length !== 1 ? 's' : ''}</span>
        <div className="relative w-48">
          <Search className="absolute left-2 top-1.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="h-7 text-xs pl-7" data-testid="report-search" />
        </div>
      </div>
      <div className="border rounded-xl overflow-auto max-h-[500px]">
        <table className="w-full text-xs table-auto">
          <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm"><tr className="border-b">
            {allCols.map(c => (
              <SortableTableHeader key={c.key} label={c.label} field={c.key} sortState={sort} onSort={handleSort}
                className={`whitespace-nowrap ${c.align === 'right' ? 'text-right' : ''}`}
                style={widthMap[c.key] ? { width: `${widthMap[c.key]}px`, minWidth: `${widthMap[c.key]}px` } : undefined} />
            ))}
            {drillDown && <th className="px-2 w-8"></th>}
          </tr></thead>
          <tbody>
            {displayRows.map((row, i) => {
              const isLI = row._rowType === 'lineItem';
              return (
                <tr key={i} className={`border-b transition-colors ${isLI ? 'bg-slate-50/80' : 'hover:bg-accent/20'} ${drillDown && !isLI ? 'cursor-pointer' : ''}`}
                  onClick={() => handleRowNav(row)} data-testid={isLI ? 'line-item-row' : 'report-data-row'}>
                  {allCols.map(c => {
                    let val = row[c.key];
                    if (!isLI && c.key.startsWith('li_')) return <td key={c.key} className="px-3 py-1"></td>;
                    if (c.fmt) val = c.fmt(val, row);
                    else if (val === null || val === undefined) val = '\u2014';
                    else if (typeof val === 'boolean') val = val ? 'Yes' : 'No';
                    else if (isDateKey(c.key) && val) val = fmtDateTz(val, tz);
                    else if (typeof val === 'number' && (c.key.includes('value') || c.key.includes('cost') || c.key.includes('price') || c.key.includes('total') || c.key.includes('paid') || c.key.includes('outstanding') || c.key.includes('proceeds') || c.key.includes('nbv') || c.key.includes('depr')))
                      val = fmtCurrency(val);
                    const w = widthMap[c.key];
                    return (
                      <td key={c.key}
                        style={w ? { width: `${w}px`, minWidth: `${w}px` } : undefined}
                        className={`px-3 py-1.5 whitespace-nowrap ${c.align === 'right' ? 'text-right' : ''} ${c.mono ? 'font-mono' : ''} ${isLI ? 'text-[10px] text-muted-foreground' : ''}`}>
                        {val}
                      </td>
                    );
                  })}
                  {drillDown && <td className="px-2 py-1.5 text-center">{!isLI && <ExternalLink className="h-3 w-3 text-muted-foreground" />}</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  HEADER ONLY FORMAT — generic
// ═══════════════════════════════════════════════════════════════════════════

function HeaderOnlyTable({ rows, config, tz, drillDown, search, setSearch, sort, setSort, navigate, customCols }) {
  const cols = customCols || config.columns;
  const lf = config.linesField || 'line_items';

  // Build width map from columns
  const widthMap = {};
  cols.forEach(c => { if (c.width) widthMap[c.key] = c.width; });

  const enriched = useMemo(() => rows.map(r => ({
    ...r,
    _line_count: (r[lf] || []).length,
    _doc_value: computeDocValue(r, config.valueFields),
  })), [rows, lf, config.valueFields]);

  let filtered = enriched;
  if (search) { const s = search.toLowerCase(); filtered = enriched.filter(r => cols.some(c => String(getVal(r, c.key) || '').toLowerCase().includes(s))); }
  const sorted = sort.field ? sortData(filtered, sort.field, sort.dir) : filtered;

  const excludeRe = config.excludeStatuses?.length ? new RegExp(config.excludeStatuses.join('|'), 'i') : null;
  const totalVal = config.totalField
    ? enriched.filter(r => !excludeRe || !excludeRe.test(r.status)).reduce((s, r) => s + computeDocValue(r, config.valueFields), 0)
    : null;

  const handleRowNav = (row) => {
    if (!drillDown) return;
    const val = row[drillDown.keyField]; if (!val) return;
    if (drillDown.param) navigate(`${drillDown.route}?${drillDown.param}=${encodeURIComponent(val)}`);
    else navigate(`${drillDown.route}/${encodeURIComponent(val)}`);
  };

  return (
    <div data-testid="header-only-report">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-muted-foreground">{filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>
        <div className="relative w-48">
          <Search className="absolute left-2 top-1.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="h-7 text-xs pl-7" data-testid="report-search" />
        </div>
      </div>
      <div className="border rounded-xl overflow-auto max-h-[500px]">
        <table className="w-full text-xs table-auto">
          <thead className="sticky top-0 bg-slate-800 text-white"><tr>
            {cols.map(c => (
              <SortableTableHeader key={c.key} label={c.label} field={c.key} sortState={sort} onSort={(f, d) => setSort({ field: f, dir: d })}
                className={`whitespace-nowrap font-bold ${c.align === 'right' ? 'text-right' : ''}`}
                style={widthMap[c.key] ? { width: `${widthMap[c.key]}px`, minWidth: `${widthMap[c.key]}px` } : undefined} />
            ))}
            {drillDown && <th className="px-2 w-8"></th>}
          </tr></thead>
          <tbody>
            {sorted.slice(0, 500).map((row, i) => (
              <tr key={i} className={`border-b hover:bg-accent/20 ${drillDown ? 'cursor-pointer' : ''}`}
                onClick={() => handleRowNav(row)} data-testid="report-data-row">
                {cols.map(c => (
                  <td key={c.key}
                    style={widthMap[c.key] ? { width: `${widthMap[c.key]}px`, minWidth: `${widthMap[c.key]}px` } : undefined}
                    className={`px-3 py-1.5 whitespace-nowrap ${c.align === 'right' ? 'text-right' : ''} ${c.mono ? 'font-mono' : ''}`}>
                    {fmtCell(getVal(row, c.key), c, row, tz)}
                  </td>
                ))}
                {drillDown && <td className="px-2 py-1.5 text-center"><ExternalLink className="h-3 w-3 text-muted-foreground" /></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-3 px-1">
        <span className="text-xs text-muted-foreground font-medium">Total Records: {enriched.length}</span>
        {totalVal !== null && (
          <div className="text-right">
            <span className="text-xs font-bold mr-2">{config.totalLabel}</span>
            <span className="text-xs font-bold">{totalVal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        )}
      </div>
      {config.noteText && <p className="text-[10px] text-muted-foreground mt-2 px-1">Note: {config.noteText}</p>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  LINE ONLY FORMAT — generic
// ═══════════════════════════════════════════════════════════════════════════

function LineOnlyTable({ rows, config, tz, search, setSearch, sort, setSort, customCols }) {
  const cols = customCols || config.columns;
  const lf = config.linesField || 'line_items';

  // Build width map from columns
  const widthMap = {};
  cols.forEach(c => { if (c.width) widthMap[c.key] = c.width; });

  const flatRows = useMemo(() => {
    const pf = config.parentFields || [];
    const result = [];
    for (const r of rows) {
      for (const li of (r[lf] || [])) {
        const flat = { _currency: r.currency || 'USD', _isCancelled: config.excludeStatuses?.some(s => new RegExp(s, 'i').test(r.status)) };
        // Copy parent-level fields
        for (const f of pf) flat[f] = r[f] || '';
        // Copy all line-item fields
        for (const k of Object.keys(li)) flat[k] = li[k];
        // Computed fields
        flat.discount_pct = li.discount || 0;
        result.push(flat);
      }
    }
    return result;
  }, [rows, lf, config.parentFields, config.excludeStatuses]);

  let filtered = flatRows;
  if (search) { const s = search.toLowerCase(); filtered = flatRows.filter(r => cols.some(c => String(r[c.key] || '').toLowerCase().includes(s))); }
  const sorted = sort.field ? sortData(filtered, sort.field, sort.dir) : filtered;

  const totalVal = config.totalField
    ? flatRows.filter(r => !r._isCancelled).reduce((s, r) => s + parseFloat(r[config.totalField] || 0), 0)
    : null;

  return (
    <div data-testid="line-only-report">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-muted-foreground">{filtered.length} line{filtered.length !== 1 ? 's' : ''}</span>
        <div className="relative w-48">
          <Search className="absolute left-2 top-1.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="h-7 text-xs pl-7" data-testid="report-search" />
        </div>
      </div>
      <div className="border rounded-xl overflow-auto max-h-[500px]">
        <table className="w-full text-xs table-auto">
          <thead className="sticky top-0 bg-slate-800 text-white"><tr>
            {cols.map(c => (
              <SortableTableHeader key={c.key} label={c.label} field={c.key} sortState={sort} onSort={(f, d) => setSort({ field: f, dir: d })}
                className={`whitespace-nowrap font-bold ${c.align === 'right' ? 'text-right' : ''}`}
                style={widthMap[c.key] ? { width: `${widthMap[c.key]}px`, minWidth: `${widthMap[c.key]}px` } : undefined} />
            ))}
          </tr></thead>
          <tbody>
            {sorted.slice(0, 2000).map((row, i) => (
              <tr key={i} className="border-b hover:bg-accent/20" data-testid="line-item-row">
                {cols.map(c => (
                  <td key={c.key}
                    style={widthMap[c.key] ? { width: `${widthMap[c.key]}px`, minWidth: `${widthMap[c.key]}px` } : undefined}
                    className={`px-3 py-1.5 ${c.key === 'item_description' ? '' : 'whitespace-nowrap'} ${c.align === 'right' ? 'text-right' : ''} ${c.mono ? 'font-mono' : ''}`}>
                    {fmtCell(row[c.key], c, row, tz)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-3 px-1">
        <span className="text-xs text-muted-foreground font-medium">Total Records: {flatRows.length}</span>
        {totalVal !== null && (
          <div className="text-right">
            <span className="text-xs font-bold mr-2">{config.totalLabel}</span>
            <span className="text-xs font-bold">{totalVal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        )}
      </div>
      {config.noteText && <p className="text-[10px] text-muted-foreground mt-2 px-1">Note: {config.noteText}</p>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  COMBO FORMAT — generic card layout per record
// ═══════════════════════════════════════════════════════════════════════════

function ComboFormatTable({ rows, config, tz, drillDown, search, setSearch, navigate }) {
  const lf = config.linesField || 'line_items';
  const pkField = config.primaryKeyField || drillDown?.keyField || 'order_number';
  const pkLabel = config.primaryKeyLabel || 'Order Number';

  let filtered = rows;
  if (search) {
    const s = search.toLowerCase();
    filtered = rows.filter(r =>
      String(r[pkField] || '').toLowerCase().includes(s) ||
      String(r.supplier_name || '').toLowerCase().includes(s) ||
      String(r.status || '').toLowerCase().includes(s) ||
      (r[lf] || []).some(li => String(li.item_description || '').toLowerCase().includes(s))
    );
  }

  const handleRowNav = (row) => {
    if (!drillDown) return;
    const val = row[drillDown.keyField]; if (!val) return;
    if (drillDown.param) navigate(`${drillDown.route}?${drillDown.param}=${encodeURIComponent(val)}`);
    else navigate(`${drillDown.route}/${encodeURIComponent(val)}`);
  };

  return (
    <div data-testid="combo-report">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-muted-foreground">{filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>
        <div className="relative w-48">
          <Search className="absolute left-2 top-1.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="h-7 text-xs pl-7" data-testid="report-search" />
        </div>
      </div>
      <div className="space-y-4">
        {filtered.slice(0, 200).map((rec, idx) => {
          const currency = rec.currency || 'USD';
          const docValue = config.valueFields ? computeDocValue(rec, config.valueFields) : null;
          const lines = rec[lf] || [];

          return (
            <div key={idx} className="border rounded-lg overflow-hidden bg-white" data-testid="combo-order-card">
              {/* Primary key header bar */}
              <div className={`px-4 py-2 bg-slate-100 border-b font-bold text-xs flex items-center justify-between ${drillDown ? 'cursor-pointer hover:bg-slate-200' : ''}`}
                onClick={() => handleRowNav(rec)}>
                <span>{pkLabel} : {rec[pkField] || '\u2014'}</span>
                {drillDown && <ExternalLink className="h-3 w-3 text-muted-foreground" />}
              </div>

              {/* Header metadata sections */}
              <div className={`grid grid-cols-${Math.min((config.headerSections || []).length, 4)} gap-0 border-b text-[11px]`}
                style={{ gridTemplateColumns: `repeat(${(config.headerSections || []).length}, 1fr)` }}>
                {(config.headerSections || []).map((section, si) => (
                  <div key={si} className={`px-3 py-2 ${si < (config.headerSections.length - 1) ? 'border-r' : ''}`}>
                    {section.map((field, fi) => (
                      <div key={fi} className="flex gap-1 py-0.5">
                        <span className="font-semibold text-slate-700 whitespace-nowrap">{field.label}</span>
                        <span className="text-slate-600 truncate">
                          {(() => {
                            const v = getVal(rec, field.key);
                            if (!v) return '';
                            if (isDateKey(field.key) || ['order_date', 'issued_date', 'entry_date', 'invoice_date', 'due_date', 'received_date'].includes(field.key))
                              return fmtDateOnlyTz(v, tz);
                            return String(v);
                          })()}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {/* Line items table */}
              {lines.length > 0 && (
                <div className="overflow-auto">
                  <table className="w-full text-[11px]">
                    <thead className="bg-slate-50"><tr className="border-b">
                      {(config.lineColumns || []).map((c, ci) => (
                        <th key={ci} className={`px-2 py-1.5 font-bold text-left whitespace-nowrap ${c.align === 'right' ? 'text-right' : ''}`}>{c.label}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {lines.map((li, li_idx) => (
                        <tr key={li_idx} className="border-b last:border-0">
                          {(config.lineColumns || []).map((c, ci) => {
                            let val = li[c.key];
                            if (c.pct || c.key === 'discount_pct') val = fmtPct(li.discount);
                            else if (c.currency) val = Number(val || 0).toFixed(2);
                            else if (c.qty) val = Number(val || 0).toFixed(2);
                            else if (c.key === 'item_due_date') val = val ? fmtDateOnlyTz(val, tz) : '';
                            else if (val === undefined || val === null) val = '';
                            return (
                              <td key={ci} className={`px-2 py-1 ${c.align === 'right' ? 'text-right' : ''} ${c.key === 'item_description' ? '' : 'whitespace-nowrap'}`}>
                                {String(val)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Document value footer (only if valueFields defined) */}
              {docValue !== null && (
                <div className="px-4 py-2 text-right text-xs border-t bg-slate-50">
                  <span className="font-bold">{pkLabel.replace('Number', 'Value').replace('Ref', 'Value')} ({currency}) : {docValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
